import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
} from "@mui/material";

// sale: current sale document (with items: [{itemId, isCollection, collectionId, quantity, deliveredQuantity}])
// catalog: { items: [...], collections: [...] }
// onDeliver: function(deliveryItems) to submit
// open/close: dialog state

export default function PartialDeliveryDialog({
  open,
  onClose,
  sale,
  stockMap,
  onDeliver,
}) {
  // Prepare sale items: flatten items and expand collections
  // sale.items: can be both standalones and collections
  const [deliverQuantities, setDeliverQuantities] = useState({}); // key: itemId|collectionId

  // Build all items and items-in-collections as delivery candidates
  const deliveryRows = [];
  sale?.items?.forEach((si) => {
    if (!si.isCollection) {
      // Standalone item
      const pending = si?.quantity - (si?.deliveredQuantity || 0);
      if (pending > 0) {
        deliveryRows.push({
          key: si?.item?._id,
          label: `${si?.item?.name}`,
          isCollection: false,
          itemId: si?.item?._id,
          pending,
        });
      }
    } else {
      // Whole collection as one entity
      const collection = si?.collection;
      const alreadyDelivered = si?.deliveredQuantity || 0;
      const pending = si.quantity - alreadyDelivered;
      if (pending > 0) {
        deliveryRows.push({
          key: collection._id,
          label: `${collection.name} [Set]`,
          isCollection: true,
          collectionId: collection._id,
          pending,
        });
      }
    }
  });

  const getStock = (row) => {
    // For items
    if (!row.isCollection) return stockMap[`${row.itemId}`] ?? 0;
    // For collections
    return stockMap[`COL_${row.collectionId}`] ?? 0;
  };

  const handleQtyChange = (key, max, v) => {
    let qty = parseInt(v, 10) || 0;
    if (qty > max) qty = max;
    if (qty < 0) qty = 0;
    setDeliverQuantities((q) => ({ ...q, [key]: qty }));
  };

  // On submit: prepare items array as expected by backend
  const handleSubmit = () => {
    const toDeliver = [];
    deliveryRows.forEach((row) => {
      const qty = deliverQuantities[row.key] || 0;
      if (qty > 0) {
        toDeliver.push(
          row.isCollection
            ? {
                isCollection: true,
                collection: row.collectionId,
                quantity: qty,
              }
            : {
                isCollection: false,
                item: row.itemId,
                quantity: qty,
              }
        );
      }
    });
    onDeliver(toDeliver);
    setDeliverQuantities({});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Partial/Full Delivery</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Enter quantity delivered for each item/collection.
        </Typography>
        {deliveryRows.length === 0 && (
          <Typography>No pending items to deliver.</Typography>
        )}
        {deliveryRows.map((row) => {
          const stock = getStock(row);
          const maxDeliverable = Math.min(row.pending, stock);

          return (
            <Box
              key={row.key}
              sx={{ display: "flex", alignItems: "center", mb: 4 }}
            >
              <Chip
                label={row.label}
                sx={{ mr: 2 }}
                color={row.isCollection ? "info" : "default"}
              />
              <TextField
                label="Quantity"
                type="number"
                value={deliverQuantities[row.key] || ""}
                onChange={(e) =>
                  handleQtyChange(row.key, maxDeliverable, e.target.value)
                }
                sx={{ mx: 2 }}
                inputProps={{ min: 0, max: maxDeliverable }}
                helperText={`Pending: ${row.pending} | In Stock: ${stock}`}
              />
              {stock < row.pending && (
                <Typography color="warning.main" sx={{ ml: 2 }}>
                  Limited stock!
                </Typography>
              )}
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ m: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={Object.values(deliverQuantities).every(
            (v) => !v || v === 0
          )}
        >
          Delivered Selected
        </Button>
      </DialogActions>
    </Dialog>
  );
}
